import { useState } from 'react';
import classNames from 'classnames';
import { Input } from 'antd';
import type { AutocompleteProps } from '$components/basic/Autocomplete';
import { Autocomplete } from '$components/basic/Autocomplete';
import { Image } from '$components/basic/Image';
import { Icon } from '@/components/atoms/Icon';
import { Styled, ModalStyled } from './styled';

export type SearchSuggestionsMobileProps<TSuggestion, TSection = unknown> = AutocompleteProps<
  TSuggestion,
  TSection
>;

export function SearchSuggestionsMobile<TSuggestion, TSection = unknown>({
  className,
  placeholder,
  onChange,
  onSuggestionSelected,
  ...props
}: SearchSuggestionsMobileProps<TSuggestion, TSection>) {
  const [value, setValue] = useState('');
  const [showModal, setShowModal] = useState(false);

  const toggleModal = () => setShowModal((prev) => !prev);

  const handleChange: SearchSuggestionsMobileProps<TSuggestion, TSection>['onChange'] = (e, change) => {
    onChange?.(e, change);
    setValue(change.newValue);
  };

  const handleSuggestionSelected: SearchSuggestionsMobileProps<
    TSuggestion,
    TSection
  >['onSuggestionSelected'] = (e, data) => {
    onSuggestionSelected?.(e, data);
    toggleModal();
  };

  const searchIcon = (
    <div className="search-icon">
      <Image
        provider="cloudinary"
        src="/icons/search_tcabyq.svg"
        alt="search"
        layout="fixed"
        width="19"
        height="20"
      />
    </div>
  );

  const backButton = (
    <Icon
      id="arrows/chevron-left"
      legacy={false}
      onClick={toggleModal}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          toggleModal();
        }
      }}
      role="button"
      width={36}
      tabIndex={0}
    />
  );

  return (
    <Styled className={classNames('SearchSuggestionsMobile', className)}>
      <Input
        className="search-input"
        value={value}
        placeholder={placeholder}
        prefix={searchIcon}
        onClick={toggleModal}
      />
      <ModalStyled visible={showModal} onCancel={toggleModal} closable={false} title={null} destroyOnClose>
        <Autocomplete<TSuggestion, TSection>
          initialValue={value}
          placeholder={placeholder}
          addonBefore={backButton}
          onChange={handleChange}
          onSuggestionSelected={handleSuggestionSelected}
          alwaysRenderSuggestions
          autoFocus
          {...props}
        />
      </ModalStyled>
    </Styled>
  );
}
SearchSuggestionsMobile.displayName = 'SearchSuggestionsMobile';
